import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { IconComponent } from '@shared/icons/icon/icon.component';
import { ShellStateService } from '../shell/shell-state.service';
import { ThemeToggleComponent } from './theme-toggle/theme-toggle.component';

interface Crumb {
  labelKey: string;
  link?: string;
}

const PLATFORM_PAGES: Record<string, string> = {
  tenants: 'SIDEBAR.NAV.TENANTS',
  accounts: 'SIDEBAR.NAV.ACCOUNTS_MANAGEMENT',
  groups: 'SIDEBAR.NAV.GROUPS',
};

const CONSOLE_PAGES: Record<string, string> = {
  dashboard: 'NAV.DASHBOARD',
  agents: 'NAV.AGENTS',
  'api-keys': 'NAV.API_KEYS',
  monitoring: 'NAV.MONITORING',
  'audit-logs': 'NAV.AUDIT_LOGS',
};

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

/** Breadcrumb trail for a URL, e.g. `/groups/42` → Platform / Groups (+ detail label). */
export function breadcrumbFor(url: string): Crumb[] {
  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
  const [first] = segments;
  if (first && PLATFORM_PAGES[first]) {
    const page: Crumb = { labelKey: PLATFORM_PAGES[first] };
    if (segments.length > 1) page.link = `/${first}`;
    return [{ labelKey: 'SIDEBAR.SECTION_PLATFORM' }, page];
  }
  if (first === 'console') {
    const page = segments.find((s) => CONSOLE_PAGES[s]);
    return [
      { labelKey: 'SIDEBAR.SECTION_AI' },
      ...(page ? [{ labelKey: CONSOLE_PAGES[page] }] : []),
    ];
  }
  if (first === 'help') return [{ labelKey: 'SIDEBAR.NAV.HELP' }];
  return [];
}

@Component({
  selector: 'mtm-app-header',
  standalone: true,
  imports: [RouterLink, TranslatePipe, IconComponent, ThemeToggleComponent],
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeaderComponent {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  readonly shell = inject(ShellStateService);

  readonly languages = LANGUAGES;

  private readonly _currentUrl = signal(this.router.url);

  readonly currentLang = signal(this.translate.currentLang ?? 'en');

  readonly crumbs = computed(() => breadcrumbFor(this._currentUrl()));

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        this._currentUrl.set(e.urlAfterRedirects);
        this.shell.mobileNavOpen.set(false);
      });
  }

  switchLang(code: string): void {
    this.translate.use(code);
    this.currentLang.set(code);
  }
}
